import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControlLabel,
  Link,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";

import {
  countSentence,
  indexBlocked,
  markSentence,
  missingTags,
  needsSentence,
  pendingFor,
} from "../access-marks.js";
import ErrorState from "./ErrorState.jsx";
import Loading from "./Loading.jsx";

/**
 * What one person who would be refused lacks, and what can be done about it.
 *
 * The mark says it in words as well as in the amber, because a state that is
 * only a colour is not a state a colour-blind operator can read. The actions
 * come from the `links` the host gives: a host that cannot grant a tag or
 * open a request (Builder) passes none and gets the sentence without the way
 * on, and the sentence still says what is missing.
 */
function BlockedMark({ item, requests, links }) {
  const tags = missingTags(item.missing);
  return (
    <Stack spacing={0.5} data-blocked-user={item.user}>
      <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap" }}>
        {/* The frame is the colour and the word is in the ordinary text colour:
            amber as text on white falls short of 4.5:1. */}
        <Chip size="small" variant="outlined" label="Would be refused"
          sx={{ color: "text.primary", borderColor: "kernel.attention" }} />
        <Typography variant="body2">{markSentence(item.missing)}</Typography>
      </Stack>
      {tags.map((entry) => {
        const pending = pendingFor(requests, item.user, entry.tag);
        return (
          <Stack key={entry.tag} direction="row" spacing={1.5}
            sx={{ alignItems: "baseline", flexWrap: "wrap", pl: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              Needs {needsSentence(entry)}.
            </Typography>
            {pending ? (
              links?.request ? (
                <Link href={links.request(pending)} underline="always" variant="caption">
                  They asked for {entry.tag}: review the request
                </Link>
              ) : (
                <Typography variant="caption" color="text.secondary">
                  They have asked for {entry.tag}.
                </Typography>
              )
            ) : links?.grant ? (
              <Link href={links.grant({ user: item.user, tag: entry.tag })}
                underline="always" variant="caption">
                Grant {entry.tag}
              </Link>
            ) : null}
          </Stack>
        );
      })}
    </Stack>
  );
}

/**
 * The line at the top: how many people on this list would be refused. It says
 * what the number is of and what it is not a promise about, on the number —
 * and when the kernel would not say, it says that, which is a different
 * sentence from "nobody is refused".
 */
function BlockedSummary({ blocked, failure, listed, total }) {
  if (failure) {
    return (
      <Typography variant="caption" color="text.secondary" data-blocked-summary="unavailable">
        {failure.status === 403
          ? "Who would be refused is shown to the person who requested this app and to governors, and you are neither."
          : `Who would be refused could not be read just now (${failure.message}).`}
      </Typography>
    );
  }
  if (!blocked) return null;
  const count = Number(blocked.count) || 0;
  return (
    <Alert severity={count ? "warning" : "success"} variant="outlined"
      data-blocked-summary={count ? "some" : "none"}>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {countSentence({ count, total: blocked.total ?? total, listed })}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
        As of this read, from each person's tags and what the app needs.
        A tag granted later clears a mark, and somebody just given one may
        still see the screen until they sign in again.
      </Typography>
    </Alert>
  );
}

/**
 * Who may use one app. The kernel keeps the list and enforces it on every
 * call through the app; this reads and changes it with the viewer's own
 * token, so the kernel decides whether they may (the app's requester, or a
 * governor) and a governor's decision shows as locked.
 *
 * It also marks the people who would be refused (LO-210): somebody on the
 * list who lacks a tag the app needs gets the missing-permissions screen, not
 * the app, and the person who manages the list is the one who can do
 * something about it. The marks come from `GET /apps/{app}/blocked-users` and
 * the pending requests from `GET /access-requests`; neither is computed here.
 * `links` is `{grant({user, tag}), request({id})}`, each returning an
 * address, for a host that can send somebody to grant the tag or to a request.
 */
export default function AppAccess({ appType, get, post, links }) {
  const [access, setAccess] = useState(null);
  const [failure, setFailure] = useState(null);
  const [email, setEmail] = useState("");
  const [saving, setSaving] = useState(false);
  const [blocked, setBlocked] = useState(null);
  const [blockedFailure, setBlockedFailure] = useState(null);
  const [requests, setRequests] = useState([]);
  const load = useCallback(() => {
    get(`/apps/${appType}/access`).then((a) => { setAccess(a); setFailure(null); })
      .catch(setFailure);
  }, [get, appType]);
  // Best effort, and a failure is shown as one: the list is what somebody came
  // for, and the marks are about it.
  const loadBlocked = useCallback(() => {
    get(`/apps/${appType}/blocked-users`)
      .then((b) => { setBlocked(b); setBlockedFailure(null); })
      .catch((e) => { setBlocked(null); setBlockedFailure(e); });
    get("/access-requests", { state: "pending" })
      .then((b) => setRequests(b?.items || []))
      .catch(() => setRequests([]));
  }, [get, appType]);
  useEffect(() => { load(); loadBlocked(); }, [load, loadBlocked]);
  const change = async (body) => {
    setSaving(true);
    try {
      setAccess(await post(`/apps/${appType}/access`, body));
      setFailure(null);
      setEmail("");
      loadBlocked();
    } catch (e) {
      setFailure(e);
    } finally {
      setSaving(false);
    }
  };
  if (!access && !failure) return <Loading label="Reading who can use it" minHeight={60} size={20} />;
  const listed = access?.mode === "listed";
  const locks = access?.locks || {};
  const may = Boolean(access?.may_manage);
  const index = indexBlocked(blocked?.items);
  // On a list, a mark sits on the person's row. Anybody refused who has no
  // row — the app is open to everyone, or the list changed since — is shown
  // in a list of their own rather than left out of a count that includes them.
  const onList = new Set(listed ? access.users : []);
  const apart = (blocked?.items || []).filter((i) => i?.user && !onList.has(String(i.user).toLowerCase()));
  return (
    <Stack spacing={1.25}>
      {failure ? <ErrorState failure={failure} /> : null}
      {access ? (
        <>
          <BlockedSummary blocked={blocked} failure={blockedFailure} listed={listed}
            total={listed ? access.users.length : undefined} />
          <FormControlLabel
            control={<Switch checked={listed} disabled={!may || saving || Boolean(locks["#mode"] && !access.as_governor)}
              onChange={(e) => change({ op: "mode", mode: e.target.checked ? "listed" : "everyone" })} />}
            label={listed ? "Only the people listed" : "Everyone in the organization"} />
          {locks["#mode"] ? (
            <Typography variant="caption" color="text.secondary">A governor set this, so only a governor can change it.</Typography>
          ) : null}
          {listed ? (
            <Stack spacing={0.75}>
              {access.users.map((u) => (
                <Stack key={u} spacing={0.5}>
                  <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap" }}>
                    <Typography variant="body2" sx={{ fontFamily: "monospace", overflowWrap: "anywhere" }}>{u}</Typography>
                    {locks[u] ? <Chip size="small" variant="outlined" label="set by a governor" /> : null}
                    {`user:${u}` === access.owner ? <Chip size="small" variant="outlined" label="requested it" /> : null}
                    {may && (!locks[u] || access.as_governor) ? (
                      <Button size="small" color="warning" disabled={saving}
                        onClick={() => change({ op: "remove", username: u })}>Remove</Button>
                    ) : null}
                  </Stack>
                  {index.get(u.toLowerCase()) ? (
                    <BlockedMark item={index.get(u.toLowerCase())} requests={requests} links={links} />
                  ) : null}
                </Stack>
              ))}
              {!access.users.length ? <Typography variant="body2" color="text.secondary">Nobody yet.</Typography> : null}
              {may ? (
                <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", alignItems: "center" }}>
                  <TextField size="small" placeholder="name@company.com" value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    slotProps={{ htmlInput: { "aria-label": "Email of a person to add" } }} />
                  <Button size="small" variant="outlined" disabled={saving || !email.trim()}
                    onClick={() => change({ op: "add", username: email.trim() })}>Add</Button>
                </Stack>
              ) : null}
            </Stack>
          ) : null}
          {apart.length ? (
            <Box>
              <Typography variant="overline" color="text.secondary" sx={{ display: "block" }}>
                {listed ? "Also refused, and not on the list" : "People who would be refused"}
              </Typography>
              {/* Its own scroll container, and a focus stop so the keyboard
                  can scroll it: a long list must not lengthen the drawer. */}
              <Box tabIndex={0} role="region" aria-label="People who would be refused"
                sx={{ maxHeight: 320, overflowY: "auto", pr: 1 }}>
                <Stack spacing={1.25}>
                  {apart.map((item) => (
                    <Stack key={item.user} spacing={0.5}>
                      <Typography variant="body2" sx={{ fontFamily: "monospace", overflowWrap: "anywhere" }}>
                        {item.user}
                      </Typography>
                      <BlockedMark item={item} requests={requests} links={links} />
                    </Stack>
                  ))}
                </Stack>
              </Box>
            </Box>
          ) : null}
          {!may ? (
            <Typography variant="caption" color="text.secondary">
              Only the person who requested this app, or a governor, can change this.
            </Typography>
          ) : null}
        </>
      ) : null}
      {access ? (
        <Typography variant="caption" color="text.secondary">
          Being on the list opens the app. What it shows is still limited to
          each person's own access. Changes take effect within seconds, with
          no new approval.
        </Typography>
      ) : null}
    </Stack>
  );
}
